// Sign a transaction XDR with whichever wallet the user connected (LOBSTR
// extension, WalletConnect via the shared Navbar kit, or Freighter). Same
// branches the staking handlers use inline.
import {
  FREIGHTER_ID,
  FreighterModule,
  LOBSTR_ID,
  StellarWalletsKit,
  WalletNetwork,
} from "@creit.tech/stellar-wallets-kit";
import { WALLET_CONNECT_ID } from "@creit.tech/stellar-wallets-kit/modules/walletconnect.module";
import { signTransaction } from "@lobstrco/signer-extension-api";
import { walletTypes } from "../enums";
import { kit } from "../components/Navbar";

// WalletConnect gives up on a request after 5 minutes; stop waiting a little
// later so a request lost on the way can never leave a button spinning forever.
const WALLET_WAIT_MS = 5.5 * 60_000;
export const WALLET_WAIT_STOPPED = "Stopped waiting for your wallet.";

/**
 * Sign with the user's wallet. Rejects with a readable message if the wallet
 * never answers, or as soon as `cancel` resolves (the user pressed Cancel).
 */
export async function signWithUserWallet(
  walletName: string | null | undefined,
  address: string,
  txXdr: string,
  cancel?: Promise<void>
): Promise<string> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  const timeout = new Promise<never>((_, reject) => {
    timer = setTimeout(
      () => reject(new Error("No answer from your wallet. Open it, or disconnect and reconnect it, then try again.")),
      WALLET_WAIT_MS
    );
  });
  const cancelled = cancel
    ? cancel.then(() => {
        throw new Error(WALLET_WAIT_STOPPED);
      })
    : new Promise<never>(() => {});
  try {
    return await Promise.race([signRaw(walletName, address, txXdr), timeout, cancelled]);
  } finally {
    clearTimeout(timer);
  }
}

async function signRaw(
  walletName: string | null | undefined,
  address: string,
  txXdr: string
): Promise<string> {
  if (walletName === (LOBSTR_ID as any) || walletName === (walletTypes.LOBSTR as any)) {
    return signTransaction(txXdr);
  }
  if (
    walletName === walletTypes.WALLETCONNECT ||
    walletName === (WALLET_CONNECT_ID as any) ||
    walletName === "wallet_connect"
  ) {
    await kit.setWallet(WALLET_CONNECT_ID);
    const { signedTxXdr } = await kit.signTransaction(txXdr, {
      address,
      networkPassphrase: WalletNetwork.PUBLIC,
    });
    return signedTxXdr;
  }
  const freighterKit = new StellarWalletsKit({
    network: WalletNetwork.PUBLIC,
    selectedWalletId: FREIGHTER_ID,
    modules: [new FreighterModule()],
  });
  const { signedTxXdr } = await freighterKit.signTransaction(txXdr, {
    address,
    networkPassphrase: WalletNetwork.PUBLIC,
  });
  return signedTxXdr;
}
